import { useState, useEffect } from 'react'
import { HomeworkService, type Homework } from '../services/homework'
import { VideoService } from '../services/videos'
import type { VideoWithCategory } from '../services/videos'
import { CategoryService } from '../services/categories'
import type { CategoryWithVideoCount } from '../services/categories'
import { SchoolService } from '../services/schools'
import type { School } from '../services/schools'
import { jstEndOfDayISO } from '../utils/jstDate'

// ─── Types ─────────────────────────────────────────────────────────────────────

interface HomeworkForm {
  category: string
  title: string
  description: string
  related_video_id: string | null
  display_order: number
  is_active: boolean
  deadline_at: string
  start_at: string
  target_school_ids: string[]
}

const emptyForm = (): HomeworkForm => ({
  category: '',
  title: '',
  description: '',
  related_video_id: null,
  display_order: 0,
  is_active: true,
  deadline_at: '',
  start_at: '',
  target_school_ids: [],
})

// ─── Component ─────────────────────────────────────────────────────────────────

const AdminHomeworkManager = ({ type }: { type: 'system' | 'entre' }) => {
  const [homeworkList, setHomeworkList] = useState<Homework[]>([])
  const [videos, setVideos] = useState<VideoWithCategory[]>([])
  const [categories, setCategories] = useState<CategoryWithVideoCount[]>([])
  const [selectedCategory, setSelectedCategory] = useState('')
  const [schools, setSchools] = useState<School[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<HomeworkForm>(emptyForm())
  const [saveMessage, setSaveMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    setIsEditing(false)
  }, [type])

  const loadData = async () => {
    setIsLoading(true)
    try {
      const [hw, vids, cats, schoolList] = await Promise.all([
        HomeworkService.getAllHomework(),
        VideoService.getVideos(),
        CategoryService.getCategories(),
        SchoolService.getSchools(),
      ])
      setHomeworkList(hw)
      setVideos(vids)
      setCategories(cats)
      setSchools(schoolList)
    } catch (err) {
      console.error(err)
      setError('データの読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const openNew = () => {
    setForm(emptyForm())
    setSelectedCategory('')
    setEditingId(null)
    setIsEditing(true)
    setSaveMessage('')
    setError('')
  }

  const openEdit = (hw: Homework) => {
    setForm({
      category: hw.category || '',
      title: hw.title,
      description: hw.description || '',
      related_video_id: hw.related_video_id,
      display_order: hw.display_order,
      is_active: hw.is_active,
      deadline_at: hw.deadline_at ? hw.deadline_at.slice(0, 10) : '',
      start_at: hw.start_at ? hw.start_at.slice(0, 10) : '',
      target_school_ids: hw.target_school_ids ?? [],
    })
    const cat = hw.related_video_id
      ? (videos.find(v => v.id === hw.related_video_id)?.category_id ?? '')
      : ''
    setSelectedCategory(cat)
    setEditingId(hw.id)
    setIsEditing(true)
    setSaveMessage('')
    setError('')
  }

  const handleSave = async () => {
    if (!form.title.trim()) { setError('タイトルは必須です'); return }
    if (type === 'entre' && !form.deadline_at) { setError('締切日は必須です'); return }
    setError('')

    const isEntre = type === 'entre'

    const payload = {
      type: type as 'system' | 'entre',
      category: form.category || null,
      title: form.title,
      description: form.description || null,
      related_video_id: form.related_video_id,
      scoring_prompt: '',
      initial_message: null,
      display_order: form.display_order,
      is_active: form.is_active,
      school_id: null,
      deadline_at: isEntre && form.deadline_at ? jstEndOfDayISO(form.deadline_at) : null,
      start_at: isEntre && form.start_at ? form.start_at : null,
      target_grades: [] as string[],
      target_classes: [] as string[],
      target_school_ids: isEntre ? form.target_school_ids : [] as string[],
    }

    try {
      if (editingId) {
        await HomeworkService.updateHomework(editingId, payload)
      } else {
        await HomeworkService.createHomework(payload)
      }
      setSaveMessage('保存しました')
      await loadData()
      setIsEditing(false)
    } catch (err) {
      console.error(err)
      setError('保存に失敗しました')
    }
  }

  const handleDelete = async (hw: Homework) => {
    if (!window.confirm(`「${hw.title}」を削除しますか？`)) return
    try {
      await HomeworkService.deleteHomework(hw.id)
      await loadData()
    } catch (err) {
      console.error(err)
      setError('削除に失敗しました')
    }
  }

  const toggleActive = async (hw: Homework) => {
    try {
      await HomeworkService.updateHomework(hw.id, { is_active: !hw.is_active })
      await loadData()
    } catch (err) {
      console.error(err)
    }
  }

  const visibleList = homeworkList.filter(hw =>
    type === 'system' ? hw.type === 'system' : hw.type === 'entre'
  )

  const fieldClass = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500'
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1'

  return (
    <div className="p-6">
      <div className="flex items-center gap-4 mb-6">
        {type === 'entre' && (
          <p className="text-sm text-gray-500">
            採点は先生モードで行います（画面右上の「先生モード」→「学校切替」→「宿題」→「アントレ部宿題」タブ）。
          </p>
        )}
        <button
          onClick={openNew}
          className="ml-auto px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700"
        >
          + 新規作成
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
          {error}
        </div>
      )}
      {saveMessage && !isEditing && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 text-sm">
          {saveMessage}
        </div>
      )}

      {/* Form */}
      {isEditing && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            {editingId ? '宿題を編集' : '新しい宿題を作成'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>タイトル *</label>
              <input type="text" value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className={fieldClass} placeholder="宿題のタイトル" />
            </div>
            {type === 'system' && (
              <div>
                <label className={labelClass}>カテゴリ</label>
                <input type="text" value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className={fieldClass} placeholder="例: ビジネスアイデア" />
              </div>
            )}
            <div className="md:col-span-2">
              <label className={labelClass}>説明</label>
              <textarea value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className={fieldClass} rows={2}
                placeholder="宿題の説明（生徒に表示されます）" />
            </div>
            {type === 'entre' && (
              <>
                <div>
                  <label className={labelClass}>開始日（省略可）</label>
                  <input type="date" value={form.start_at}
                    onChange={e => setForm(f => ({ ...f, start_at: e.target.value }))}
                    className={fieldClass} />
                </div>
                <div>
                  <label className={labelClass}>締切日 *</label>
                  <input type="date" value={form.deadline_at}
                    onChange={e => setForm(f => ({ ...f, deadline_at: e.target.value }))}
                    className={fieldClass} />
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>対象学校（空欄 = 全学校）</label>
                  <div className="border border-gray-200 rounded-md p-3 max-h-40 overflow-y-auto space-y-1">
                    {schools.map(school => (
                      <label key={school.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox"
                          checked={form.target_school_ids.includes(school.id)}
                          onChange={e => setForm(f => ({
                            ...f,
                            target_school_ids: e.target.checked
                              ? [...f.target_school_ids, school.id]
                              : f.target_school_ids.filter(id => id !== school.id)
                          }))}
                          className="rounded" />
                        {school.name}
                      </label>
                    ))}
                    {schools.length === 0 && (
                      <p className="text-xs text-gray-400">学校がありません</p>
                    )}
                  </div>
                </div>
              </>
            )}

            <div>
              <label className={labelClass}>関連動画 — カテゴリ</label>
              <select
                value={selectedCategory}
                onChange={e => {
                  setSelectedCategory(e.target.value)
                  setForm(f => ({ ...f, related_video_id: null }))
                }}
                className={fieldClass}
              >
                <option value="">カテゴリを選択</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>関連動画</label>
              <select
                value={form.related_video_id || ''}
                onChange={e => setForm(f => ({ ...f, related_video_id: e.target.value || null }))}
                className={fieldClass}
                disabled={!selectedCategory}
              >
                <option value="">なし</option>
                {videos
                  .filter(v => v.category_id === selectedCategory)
                  .map(v => (
                    <option key={v.id} value={v.id}>{v.title}</option>
                  ))}
              </select>
            </div>

            {type === 'system' && (
              <div>
                <label className={labelClass}>表示順</label>
                <input type="number" value={form.display_order}
                  onChange={e => setForm(f => ({ ...f, display_order: Number(e.target.value) }))}
                  className={fieldClass} />
              </div>
            )}

            <div className="flex items-center gap-2">
              <input type="checkbox" id="is_active" checked={form.is_active}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                className="rounded" />
              <label htmlFor="is_active" className="text-sm text-gray-700">公開する</label>
            </div>
          </div>

          {error && <p className="text-red-600 text-sm mt-3">{error}</p>}

          <div className="flex gap-3 mt-4">
            <button onClick={handleSave}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700">
              保存
            </button>
            <button onClick={() => setIsEditing(false)}
              className="px-5 py-2 bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-300">
              キャンセル
            </button>
          </div>
        </div>
      )}

      {/* Homework list */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          {visibleList.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>宿題がまだありません。「新規作成」から追加してください。</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">タイトル</th>
                  {type === 'system' && (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">カテゴリ</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">順</th>
                    </>
                  )}
                  {type === 'entre' && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">締切</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">公開</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {visibleList.map(hw => (
                  <tr key={hw.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{hw.title}</p>
                      {hw.description && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{hw.description}</p>
                      )}
                    </td>
                    {type === 'system' && (
                      <>
                        <td className="px-4 py-3 text-sm text-gray-600">{hw.category || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{hw.display_order}</td>
                      </>
                    )}
                    {type === 'entre' && (
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {hw.deadline_at
                          ? new Date(hw.deadline_at).toLocaleDateString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric' })
                          : '—'}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleActive(hw)}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                          hw.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {hw.is_active ? '公開中' : '非公開'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button onClick={() => openEdit(hw)} className="text-sm text-blue-600 hover:underline">編集</button>
                      <button onClick={() => handleDelete(hw)} className="text-sm text-red-600 hover:underline">削除</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

export default AdminHomeworkManager
